import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Divider,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import { useParams, useNavigate } from "react-router-dom";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";

import upsImage from "../assets/ups.png";

export default function CreateShippingLabel() {
  const { orderId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Sample order data - replace with actual data
  const [orderData] = useState({
    orderId: orderId || "ORD-123456",
    pricing: {
      platformFee: 0.5,
    },
    shipping: {
      from: {
        name: "John Seller",
        address: "123 Main St",
        city: "Los Angeles, CA 90001",
        country: "United States",
      },
      to: {
        name: "Emily Buyer",
        address: "789 Oak Rd",
        city: "New York, NY 10022",
        country: "United States",
      },
    },
  });
  const showPackageDetailsCard = false;

  // Package details state
  const [packageDetails, setPackageDetails] = useState({
    packageType: "Box",
    weight: "",
    length: "",
    width: "",
    height: "",
    declaredValue: "",
  });

  // Shipping service state
  const [selectedShippingService, setSelectedShippingService] = useState(
    "ups-ground"
  );

  // Shipping service options
  const shippingOptions = [
    {
      id: "ups-ground",
      title: "UPS Ground",
      description: "Most affordable option",
      deliveryTime: "3-5 Business Days",
      price: "$8.45",
      numericPrice: 8.45,
    },
    {
      id: "ups-2nd-day",
      title: "UPS 2nd Day Air",
      description: "Faster delivery at a moderate cost",
      deliveryTime: "2 Business Days",
      price: "$14.20",
      numericPrice: 14.2,
    },
    {
      id: "ups-next-day",
      title: "UPS Next Day Air",
      description: "Fastest delivery option",
      deliveryTime: "1 Business Day",
      price: "$24.90",
      numericPrice: 24.9,
    },
  ];

  // Calculate current pricing based on selected shipping service
  const getCurrentPricing = () => {
    const selectedOption = shippingOptions.find(
      (option) => option.id === selectedShippingService
    );
    const shippingCost = selectedOption ? selectedOption.numericPrice : 8.45;
    const total = shippingCost + orderData.pricing.platformFee;

    return {
      shippingCost,
      platformFee: orderData.pricing.platformFee,
      total,
    };
  };

  const currentPricing = getCurrentPricing();

  const handlePackageChange = (field, value) => {
    setPackageDetails((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Static steps data
  const steps = [
    {
      id: 1,
      title: "Order Confirmed",
      description: "Buyer has completed payment",
    },
    {
      id: 2,
      title: "Create Label",
      description: "Generate your UPS shipping label",
    },
    {
      id: 3,
      title: "Ship Item",
      description: "Pack and hand over to UPS",
    },
  ];

  const handleCreateLabel = () => {
    // Navigate to showing the shipping label for download and print page
    navigate(`/seller/orders/${orderData.orderId}/shipping-label`, {
      state: { selectedShippingService },
    });
  };
  console.log("PackageDetails______", packageDetails);
  console.log("SelectedShippingService_______", selectedShippingService);
  return (
    <>
      <Helmet>
        <title>Create Shipping Label - {orderData.orderId} | R3PLAY</title>
        <meta
          name="description"
          content="Create shipping label for your order"
        />
      </Helmet>

      <Box
        sx={{
          backgroundColor: "#F5F5F5",
          minHeight: "100vh",
          py: 4,
        }}
      >
        <Box
          sx={{
            maxWidth: "1440px",
            px: "100px",
            "@media (max-width: 1024px)": {
              px: "16px",
            },
          }}
        >
          <Box sx={{ boxShadow: "0px 4px 12px 0px rgba(0, 0, 0, 0.08)" }}>
            {/* Header */}
            <Box
              sx={{
                backgroundColor: "#FFFFFF",
                py: 2,
              }}
            >
              {/* Order Status */}
              <Box sx={{ px: 3 }}>
                <Typography
                  sx={{
                    fontSize: "24px",
                    fontWeight: 600,
                    color: "#000",
                  }}
                >
                  Create Shipping Label
                </Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#666",
                    mb: 2,
                  }}
                >
                  Generate your UPS shipping label
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {/* Progress Steps */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  px: 3,
                  justifyContent: "center",
                  "@media (max-width: 768px)": {
                    flexDirection: "column",
                    gap: 2,
                  },
                }}
              >
                {steps.map((step, index) => {
                  const isCompletedStep = step.id <= 2; // Steps 1 and 2 are completed
                  const isCurrentStep = step.id === 2; // Step 2 is current (Create Label)
                  return (
                    <React.Fragment key={step.id}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          backgroundColor: isCompletedStep
                            ? "#EF5658"
                            : "#FAFAFA",
                          borderRadius: "40px",
                          px: 2,
                          width: "287px",
                          height: "58px",
                          "@media (max-width: 768px)": {
                            width: "329px",
                          },
                        }}
                      >
                        {/* Icon with circle background */}
                        <Box
                          sx={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "50%",
                            backgroundColor: isCompletedStep
                              ? "#F35D5F"
                              : "#F5F5F5",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mr: 2,
                            flexShrink: 0,
                          }}
                        >
                          <CheckOutlinedIcon
                            sx={{
                              color: isCompletedStep ? "#FFFFFF" : "#666",
                              fontSize: "24px",
                            }}
                          />
                        </Box>

                        {/* Text */}
                        <Box sx={{ flex: 1, alignItems: "center" }}>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 600,
                              color: isCompletedStep ? "#FFFFFF" : "#000",
                              lineHeight: 1.5,
                            }}
                          >
                            {step.title}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              color: isCompletedStep ? "#FFFFFF" : "#666",
                              lineHeight: 1.8,
                              "@media (min-width: 768px) and (max-width: 1160px)": {
                                display: "none",
                              },
                            }}
                          >
                            {step.description}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Horizontal Divider between steps */}
                      {index < steps.length - 1 && (
                        <Box
                          sx={{
                            width: "40px",
                            height: "1px",
                            backgroundColor: "rgba(0, 0, 0, 0.12)",
                            "@media (max-width: 768px)": {
                              display: "none", // Hide dividers on mobile
                            },
                          }}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </Box>
            </Box>

            {/* Main Content Area */}
            <Box
              sx={{
                backgroundColor: "#F0F0F0",
                p: 3,
                "@media (max-width: 768px)": {
                  p: 2,
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 3,
                  "@media (max-width: 1439px)": {
                    flexDirection: "column",
                  },
                }}
              >
                {/* Left Column - Shipping Details */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                    flex: 1,
                  }}
                >
                  {/* Shipping Details Card */}
                  <Box
                    sx={{
                      backgroundColor: "#FFFFFF",
                      borderRadius: "8px",
                      p: 2,
                      width: "778px",
                      height: "259px",
                      "@media (max-width: 856px)": {
                        width: "100%",
                        height: "auto",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "20px",
                        fontWeight: 600,
                        color: "#000",
                      }}
                    >
                      Shipping Details
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#666",
                        mb: 1,
                      }}
                    >
                      Review pickup and delivery addresses
                    </Typography>

                    <Divider sx={{ mb: 1.5 }} />

                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        "@media (max-width: 768px)": {
                          flexDirection: "column",
                        },
                      }}
                    >
                      {/* Shipping From Box */}
                      <Box
                        sx={{
                          backgroundColor: "#FAFAFA",
                          borderRadius: "8px",
                          px: 3,
                          py: 1,
                          flex: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#000",
                          }}
                        >
                          Shipping From
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "12px",
                            color: "#666",
                            mb: 2,
                          }}
                        >
                          Pickup address for this shipment
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                          }}
                        >
                          <Box>
                            <Typography
                              sx={{
                                fontSize: "14px",
                                fontWeight: 600,
                                color: "#000",
                              }}
                            >
                              {orderData.shipping.from.name}
                            </Typography>
                            <Typography
                              sx={{ fontSize: "14px", color: "#666" }}
                            >
                              {orderData.shipping.from.address}
                            </Typography>
                            <Typography
                              sx={{ fontSize: "14px", color: "#666" }}
                            >
                              {orderData.shipping.from.city}
                            </Typography>
                            <Typography
                              sx={{ fontSize: "14px", color: "#666" }}
                            >
                              {orderData.shipping.from.country}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      {/* Shipping To Box */}
                      <Box
                        sx={{
                          backgroundColor: "#FAFAFA",
                          borderRadius: "8px",
                          px: 3,
                          py: 1,
                          flex: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#000",
                          }}
                        >
                          Shipping To
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "12px",
                            color: "#666",
                            mb: 2,
                          }}
                        >
                          Delivery address provided by the buyer
                        </Typography>
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 600,
                              color: "#000",
                            }}
                          >
                            {orderData.shipping.to.name}
                          </Typography>
                          <Typography sx={{ fontSize: "14px", color: "#666" }}>
                            {orderData.shipping.to.address}
                          </Typography>
                          <Typography sx={{ fontSize: "14px", color: "#666" }}>
                            {orderData.shipping.to.city}
                          </Typography>
                          <Typography sx={{ fontSize: "14px", color: "#666" }}>
                            {orderData.shipping.to.country}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  {/* Package Details Card - COMMENTED OUT - NOT NEEDED AT THIS TIME */}
                  {showPackageDetailsCard && (
                    <Box
                      sx={{
                        backgroundColor: "#FFFFFF",
                        borderRadius: "8px",
                        p: 2,
                        width: "778px",
                        "@media (max-width: 856px)": {
                          width: "100%",
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "20px",
                          fontWeight: 600,
                          color: "#000",
                        }}
                      >
                        Package Details
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#666",
                          mb: 1,
                        }}
                      >
                        Tell us about the package you're shipping
                      </Typography>

                      <Divider sx={{ mb: 3 }} />

                      {/* Package Type and Weight Row */}
                      <Box
                        sx={{
                          display: "flex",
                          gap: 3,
                          mb: 2,
                          "@media (max-width: 768px)": {
                            flexDirection: "column",
                            gap: 2,
                          },
                        }}
                      >
                        {/* Package Type */}
                        <Box sx={{ flex: 1 }}>
                          <FormControl
                            fullWidth
                            sx={{
                              "& .MuiOutlinedInput-root": { height: "56px" },
                              "& fieldset": {
                                borderColor: "rgba(0, 0, 0, 0.23)",
                              },
                              "& .MuiInputLabel-asterisk": {
                                color: "#ef5658 !important",
                              },
                              "& .MuiFormLabel-asterisk": {
                                color: "#ef5658 !important",
                              },
                              "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "rgba(0, 0, 0, 0.23)",
                              },
                              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#EF5658",
                                borderWidth: "2px",
                              },
                              "& .MuiInputLabel-root.Mui-focused": {
                                color: "#EF5658",
                              },
                            }}
                          >
                            <InputLabel id="package-type-label">
                              Package Type
                            </InputLabel>
                            <Select
                              labelId="package-type-label"
                              label="Package Type"
                              value={packageDetails.packageType}
                              onChange={(e) =>
                                handlePackageChange(
                                  "packageType",
                                  e.target.value
                                )
                              }
                            >
                              <MenuItem value="Box">Box</MenuItem>
                              <MenuItem value="Envelope">Envelope</MenuItem>
                              <MenuItem value="Tube">Tube</MenuItem>
                              <MenuItem value="Pak">Pak</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>

                        {/* Weight */}
                        <Box sx={{ flex: 1 }}>
                          <TextField
                            fullWidth
                            label="Weight (lbs)"
                            placeholder="e.g. 2.5"
                            variant="outlined"
                            value={packageDetails.weight}
                            onChange={(e) =>
                              handlePackageChange("weight", e.target.value)
                            }
                            sx={{
                              "& .MuiInputBase-root": { height: "56px" },
                              "& fieldset": {
                                borderColor: "rgba(0, 0, 0, 0.23)",
                              },
                              "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "rgba(0, 0, 0, 0.23)",
                              },
                              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#EF5658",
                                borderWidth: "2px",
                              },
                              "& .MuiInputLabel-root.Mui-focused": {
                                color: "#EF5658",
                              },
                            }}
                          />
                        </Box>
                      </Box>

                      {/* Dimensions */}
                      <Box sx={{ mb: 3 }}>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "#000",
                            mb: 2,
                          }}
                        >
                          Dimensions
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 2,
                            flexWrap: "wrap",
                            "@media (max-width: 768px)": {
                              flexDirection: "column",
                            },
                          }}
                        >
                          {/* Length */}
                          <Box
                            sx={{
                              width: "232px",
                              height: "45px",
                              backgroundColor: "#F5F5F5",
                              borderRadius: "8px",
                              display: "flex",
                              alignItems: "center",
                              px: 2,
                              "@media (max-width: 768px)": {
                                width: "100%",
                              },
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "14px",
                                color: "#333",
                                minWidth: "15px",
                                mr: 1,
                              }}
                            >
                              L
                            </Typography>
                            <Box
                              sx={{
                                width: "1px",
                                height: "20px",
                                backgroundColor: "#BDBDBD",
                                mr: 2,
                              }}
                            />
                            <TextField
                              placeholder=""
                              value={packageDetails.length}
                              onChange={(e) =>
                                handlePackageChange("length", e.target.value)
                              }
                              variant="standard"
                              sx={{
                                flex: 1,
                                mr: 1,
                                "& .MuiInput-underline:before": {
                                  display: "none",
                                },
                                "& .MuiInput-underline:after": {
                                  display: "none",
                                },
                                "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                                  display: "none",
                                },
                                "& .MuiInputBase-input": {
                                  fontSize: "14px",
                                  padding: 0,
                                },
                              }}
                            />
                            <Typography
                              sx={{ fontSize: "14px", color: "#666" }}
                            >
                              cm
                            </Typography>
                          </Box>

                          {/* Width */}
                          <Box
                            sx={{
                              width: "232px",
                              height: "45px",
                              backgroundColor: "#F5F5F5",
                              borderRadius: "8px",
                              display: "flex",
                              alignItems: "center",
                              px: 2,
                              "@media (max-width: 768px)": {
                                width: "100%",
                              },
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "14px",
                                color: "#333",
                                minWidth: "15px",
                                mr: 1,
                              }}
                            >
                              W
                            </Typography>
                            <Box
                              sx={{
                                width: "1px",
                                height: "20px",
                                backgroundColor: "#BDBDBD",
                                mr: 2,
                              }}
                            />
                            <TextField
                              placeholder=""
                              value={packageDetails.width}
                              onChange={(e) =>
                                handlePackageChange("width", e.target.value)
                              }
                              variant="standard"
                              sx={{
                                flex: 1,
                                mr: 1,
                                "& .MuiInput-underline:before": {
                                  display: "none",
                                },
                                "& .MuiInput-underline:after": {
                                  display: "none",
                                },
                                "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                                  display: "none",
                                },
                                "& .MuiInputBase-input": {
                                  fontSize: "14px",
                                  padding: 0,
                                },
                              }}
                            />
                            <Typography
                              sx={{ fontSize: "14px", color: "#666" }}
                            >
                              cm
                            </Typography>
                          </Box>

                          {/* Height */}
                          <Box
                            sx={{
                              width: "232px",
                              height: "45px",
                              backgroundColor: "#F5F5F5",
                              borderRadius: "8px",
                              display: "flex",
                              alignItems: "center",
                              px: 2,
                              "@media (max-width: 768px)": {
                                width: "100%",
                              },
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "14px",
                                color: "#333",
                                minWidth: "15px",
                                mr: 1,
                              }}
                            >
                              H
                            </Typography>
                            <Box
                              sx={{
                                width: "1px",
                                height: "20px",
                                backgroundColor: "#BDBDBD",
                                mr: 2,
                              }}
                            />
                            <TextField
                              placeholder=""
                              value={packageDetails.height}
                              onChange={(e) =>
                                handlePackageChange("height", e.target.value)
                              }
                              variant="standard"
                              sx={{
                                flex: 1,
                                mr: 1,
                                "& .MuiInput-underline:before": {
                                  display: "none",
                                },
                                "& .MuiInput-underline:after": {
                                  display: "none",
                                },
                                "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                                  display: "none",
                                },
                                "& .MuiInputBase-input": {
                                  fontSize: "14px",
                                  padding: 0,
                                },
                              }}
                            />
                            <Typography
                              sx={{ fontSize: "14px", color: "#666" }}
                            >
                              cm
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      {/* Declared Value */}
                      <Box sx={{ mb: 2 }}>
                        <Box
                          sx={{ display: "flex", gap: 2, alignItems: "center" }}
                        >
                          <TextField
                            label="Declared Value ($)"
                            value={packageDetails.declaredValue}
                            onChange={(e) =>
                              handlePackageChange(
                                "declaredValue",
                                e.target.value
                              )
                            }
                            sx={{
                              width: "200px",
                              "& .MuiInputBase-root": { height: "56px" },
                              "& fieldset": {
                                borderColor: "rgba(0, 0, 0, 0.23)",
                              },
                              "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "rgba(0, 0, 0, 0.23)",
                              },
                              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#EF5658",
                                borderWidth: "2px",
                              },
                              "& .MuiInputLabel-root.Mui-focused": {
                                color: "#EF5658",
                              },
                            }}
                          />
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mt: 1,
                            }}
                          >
                            <InfoOutlinedIcon
                              sx={{ fontSize: 24, color: "#666" }}
                            />
                            <Typography
                              sx={{ fontSize: "16px", color: "#666" }}
                            >
                              Used to determine insurance coverage during
                              shipping
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  )}

                  {/* Shipping Service Card */}
                  <Box
                    sx={{
                      backgroundColor: "#FFFFFF",
                      borderRadius: "8px",
                      p: 2,
                      width: "778px",
                      "@media (max-width: 856px)": {
                        width: "100%",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "20px",
                        fontWeight: 600,
                        color: "#000",
                      }}
                    >
                      Shipping Service
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#666",
                        mb: 1,
                      }}
                    >
                      Choose a delivery option for this shipment
                    </Typography>

                    <Divider sx={{ mb: 3 }} />

                    {/* Custom Option Rows */}
                    <Box sx={{ mt: -2 }}>
                      {shippingOptions.map((option) => (
                        <Box
                          key={option.id}
                          onClick={() => setSelectedShippingService(option.id)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            p: 2,
                            mb: 1,
                            borderRadius: "16px",
                            backgroundColor:
                              selectedShippingService === option.id
                                ? "#FFF1F4"
                                : "transparent",
                            cursor: "pointer",
                            "&:hover": {
                              backgroundColor:
                                selectedShippingService === option.id
                                  ? "#FFF1F4"
                                  : "#F9F9F9",
                            },
                            "@media (max-width: 800px)": {
                              flexDirection: "column",
                              alignItems: "flex-start",
                              gap: 1,
                            },
                          }}
                        >
                          {/* First Row: Radio Button + UPS Icon + Service Details */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              width: "100%",
                              "@media (max-width: 800px)": {
                                flex: 1,
                              },
                            }}
                          >
                            {/* Radio Button */}
                            <Radio
                              checked={selectedShippingService === option.id}
                              value={option.id}
                              sx={{
                                color: "#666",
                                "&.Mui-checked": {
                                  color: "#EF5658",
                                },
                              }}
                            />

                            {/* UPS Icon */}
                            <Box
                              sx={{
                                width: "48px",
                                height: "48px",
                                borderRadius: "50%",
                                backgroundColor: "#F5F5F5",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Box
                                component="img"
                                src={upsImage}
                                alt="UPS"
                                sx={{
                                  width: "25px",
                                  height: "30px",
                                }}
                              />
                            </Box>

                            {/* Service Details */}
                            <Box
                              sx={{
                                flex: 2,
                                "@media (max-width: 800px)": {
                                  flex: 1,
                                },
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: 600,
                                  color: "#000",
                                  mb: 0.5,
                                }}
                              >
                                {option.title}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "12px",
                                  color: "#666",
                                }}
                              >
                                {option.description}
                              </Typography>
                            </Box>

                            {/* Delivery Time and Price - Desktop Only */}
                            <Box
                              sx={{
                                flex: 1,
                                textAlign: "center",
                                "@media (max-width: 800px)": {
                                  display: "none",
                                },
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  color: "#000",
                                }}
                              >
                                {option.deliveryTime}
                              </Typography>
                            </Box>

                            <Box
                              sx={{
                                flex: 1,
                                textAlign: "right",
                                "@media (max-width: 800px)": {
                                  display: "none",
                                },
                              }}
                            >
                              <Typography
                                sx={{
                                  pr: "20px",
                                  fontSize: "14px",
                                  fontWeight: 500,
                                  color: "#000",
                                }}
                              >
                                {option.price}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Second Row: Delivery Time and Price - Mobile Only */}
                          <Box
                            sx={{
                              display: "none",
                              "@media (max-width: 800px)": {
                                display: "flex",
                                justifyContent: "space-between",
                                width: "100%",
                              },
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "14px",
                                color: "#000",
                                pl: 1,
                              }}
                            >
                              {option.deliveryTime}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "14px",
                                fontWeight: 500,
                                color: "#000",
                              }}
                            >
                              {option.price}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>

                {/* Right Column - Cost Summary */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: "390px",
                    "@media (max-width: 768px)": {
                      maxWidth: "390px",
                      width: "100%",
                    },
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: "#FFFFFF",
                      borderRadius: "8px",
                      p: 2,
                      height: "fit-content",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "20px",
                        fontWeight: 600,
                        color: "#000",
                      }}
                    >
                      Cost Summary
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#666",
                        mb: 1,
                      }}
                    >
                      Breakdown of shipping charges
                    </Typography>

                    <Divider sx={{ mb: 1.5 }} />

                    <Box sx={{ mb: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 2,
                        }}
                      >
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              lineHeight: 1.2,
                              color: "#000",
                            }}
                          >
                            Shipping Cost
                          </Typography>
                          <Typography sx={{ fontSize: "12px", color: "#666" }}>
                            Based on selected UPS service
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            fontSize: "16px",
                            fontWeight: 600,
                            color: "#000",
                          }}
                        >
                          ${currentPricing.shippingCost.toFixed(2)}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 2,
                        }}
                      >
                        <Box>
                          <Typography sx={{ fontSize: "14px", color: "#000" }}>
                            Platform Fee
                          </Typography>
                          <Typography sx={{ fontSize: "12px", color: "#666" }}>
                            Covers label generation and handling
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            fontSize: "16px",
                            fontWeight: 600,
                            color: "#000",
                          }}
                        >
                          ${currentPricing.platformFee.toFixed(2)}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          pt: 2,
                          borderTop: "1px solid #E0E0E0",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "16px",
                            fontWeight: 600,
                            color: "#000",
                          }}
                        >
                          Total
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "16px",
                            fontWeight: 600,
                            color: "#000",
                          }}
                        >
                          ${currentPricing.total.toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Create Label Button */}
                  <Box sx={{ pt: 2 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={<LocalShippingIcon />}
                      onClick={handleCreateLabel}
                      sx={{
                        backgroundColor: "#EF5658",
                        color: "#FFFFFF",
                        fontWeight: 600,
                        fontSize: "15px",
                        py: 1.5,
                        borderRadius: "8px",
                        textTransform: "none",
                        "&:hover": {
                          backgroundColor: "#D32F2F",
                        },
                      }}
                    >
                      Generate UPS Shipping Label
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
}
